import { ClinicAuthError } from "@auth/clinicAuthClient";
import type {
  DispatchOutcome,
  DispatchStatus,
} from "@connector/connectorClient";
import { useDispatch } from "@connector/connectorQueries";
import { useUser } from "@context/UserContext";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCircleCheck,
  faCircleExclamation,
  faPaperPlane,
  faPlugCircleXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  FieldError,
  Input,
  PaperPanel,
  SettingsCard,
  Swipe,
  type SwipeTone,
} from "@scribenote/brian-design-system";
import { useState } from "react";

const DEFAULT_GOAL = "What breed is Bella?";

/**
 * How each dispatch outcome reads: the word, and the highlighter it wears.
 * `tone: null` goes unmarked - nothing was claimed and nothing went wrong.
 * "No clinic computer connected yet" is an expected state of the world, and a
 * cancelled task never claimed to do anything.
 */
interface StatusPresentation {
  tone: SwipeTone | null;
  icon: IconProp;
  label: string;
}

const STATUS_PRESENTATION: Record<DispatchStatus, StatusPresentation> = {
  done: { tone: "working", icon: faCircleCheck, label: "Done" },
  connector_offline: {
    tone: null,
    icon: faPlugCircleXmark,
    label: "No clinic computer connected yet.",
  },
  failed: { tone: "stopped", icon: faCircleExclamation, label: "Failed" },
  timeout: {
    tone: "stopped",
    icon: faCircleExclamation,
    label: "Timed out",
  },
  cancelled: {
    tone: null,
    icon: faCircleExclamation,
    label: "Cancelled",
  },
  needs_clarification: {
    tone: "flag",
    icon: faCircleExclamation,
    label: "Needs clarification",
  },
};

const messageFor = (err: unknown): string => {
  if (err instanceof ClinicAuthError) {
    if (err.code === "network_error")
      return "Couldn't reach the server. Check your connection.";
    if (err.code === "invalid_credentials")
      return "Your session expired. Sign in again to send a task.";
  }
  return "Something went wrong sending the task. Try again.";
};

const DispatchResultView = ({ result }: { result: DispatchOutcome }) => {
  const { tone, icon, label } = STATUS_PRESENTATION[result.status];
  // "connector_offline" is an expected outcome (nothing paired yet), so its
  // friendly label stands on its own without echoing the raw server output.
  const body =
    result.status === "connector_offline"
      ? null
      : ((result.output || result.error) ?? null);

  return (
    <PaperPanel
      variant="recessed"
      className="p-3"
      data-testid="connector-dispatch-result"
    >
      <div className="flex items-start gap-3">
        <FontAwesomeIcon
          icon={icon}
          className="mt-0.5 text-annotation"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <div
            data-testid="connector-dispatch-status"
            className="text-sm font-medium"
          >
            {tone ? (
              <Swipe tone={tone}>{label}</Swipe>
            ) : (
              <span className="text-ink">{label}</span>
            )}
          </div>
          {body && (
            <p className="mt-1 text-sm wrap-anywhere text-ink-body">{body}</p>
          )}
        </div>
      </div>
    </PaperPanel>
  );
};

export const TestTaskCard = () => {
  const { isUserAuthed } = useUser();
  const [goal, setGoal] = useState(DEFAULT_GOAL);
  const dispatch = useDispatch();

  const canSend = isUserAuthed && goal.trim().length > 0 && !dispatch.isPending;
  const result = dispatch.data ?? null;
  const error = dispatch.isError ? messageFor(dispatch.error) : null;

  const handleSend = () => {
    if (!canSend) return;
    dispatch.mutate(goal.trim());
  };

  return (
    <SettingsCard
      title="Send a message (THIS IS FOR TESTING PURPOSES ONLY)"
      subtitle="Dispatch a message to the connected clinic computer and see the result."
      data-testid="connector-test-task-card"
    >
      <div className="flex flex-col gap-4">
        {!isUserAuthed && (
          <p
            data-testid="connector-test-task-locked"
            className="text-sm text-annotation"
          >
            Select your profile to send a test task.
          </p>
        )}

        <div className="flex items-start gap-3">
          <Input
            aria-label="Message to dispatch"
            value={goal}
            disabled={!isUserAuthed || dispatch.isPending}
            onChange={(e) => setGoal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSend();
            }}
            placeholder={DEFAULT_GOAL}
            data-testid="connector-test-task-input"
          />
          <Button
            onClick={handleSend}
            disabled={!canSend}
            busy={dispatch.isPending}
            data-testid="connector-test-task-send"
            className="shrink-0"
          >
            <FontAwesomeIcon icon={faPaperPlane} aria-hidden="true" />
            {dispatch.isPending ? "Sending" : "Send"}
          </Button>
        </div>

        {error && (
          <FieldError data-testid="connector-test-task-error">
            {error}
          </FieldError>
        )}

        {result && <DispatchResultView result={result} />}
      </div>
    </SettingsCard>
  );
};
